import { showOpenJointAccountModal } from '@/components/atomic/OpenJointAccountModal';
import { useToasts } from '@/components/common/Toasts';
import { showIdvModal } from '@/components/idv/IdvModal';
import { useIdvPosition } from '@/components/idv/useIdvPosition';
import { showIdvSuccessModal } from '@/components/idv/views/IdvSuccessModal';
import { showInviteModal } from '@/components/partnershipInvites/InviteModal';
import { CustomError } from '@common/errors';
import { useIsJointAccountConsentRequired } from '@common/react/atomic';
import { useShowInvitePartner } from '@common/react/partnerships';
import { getPartnerName, useCurrentUser } from '@common/react/user';

export type AccountSetupStep =
    | 'invite-partner'
    | 'idv'
    | 'joint-account-consent'
    | 'complete';

export const useAccountSetupSteps = ({
    onComplete,
    checkPartnership,
    checkJointAccountConsent,
}: {
    checkPartnership?: boolean;
    checkJointAccountConsent?: boolean;
    onComplete?: (justCompletedIdv: boolean) => void;
}) => {
    const { showSuccessToast } = useToasts();
    const showInvitePartner = useShowInvitePartner();
    const isJointAccountConsentRequired = useIsJointAccountConsentRequired();
    const idvPosition = useIdvPosition();
    const { partnerInfo, plentyJointAccountStatus } = useCurrentUser();

    const getSteps = () => {
        const steps: AccountSetupStep[] = [];

        if (checkPartnership && showInvitePartner) {
            steps.push('invite-partner');
        }

        if (idvPosition !== 'complete') {
            steps.push('idv');
        }

        if (checkJointAccountConsent && isJointAccountConsentRequired) {
            steps.push('joint-account-consent');
        }

        steps.push('complete');

        return steps;
    };

    const handleNextStep = async ({
        currentStep = getSteps()[0],
        steps = getSteps(),
    }: {
        currentStep?: AccountSetupStep;
        steps?: AccountSetupStep[];
    }) => {
        const getNextStep = () => steps[steps.indexOf(currentStep) + 1];
        const getPreviousStep = () => steps[steps.indexOf(currentStep) - 1];

        const onCompleteStep = () =>
            handleNextStep({ currentStep: getNextStep(), steps });

        const partnerConsentedToJointAccount =
            plentyJointAccountStatus === 'PARTNER_CONSENTED';
        const partnerName = getPartnerName(partnerInfo, 'your partner');

        switch (currentStep) {
            case 'invite-partner':
                showInviteModal({
                    onComplete: onCompleteStep,
                    title: `Invite ${partnerName} to start investing together`,
                    showProgressBar: true,
                });
                break;
            case 'idv':
                showIdvModal({
                    onComplete: onCompleteStep,
                    includeInviteStep: steps.includes('invite-partner'),
                    title: steps.includes('joint-account-consent') ? (
                        <>
                            To open a joint investing account with {partnerName}
                            , <br />
                            we need to verify your identity.
                        </>
                    ) : (
                        <>
                            To open an investing account with Plenty, <br />
                            we need to verify your identity.
                        </>
                    ),
                    subtitle: `${
                        partnerConsentedToJointAccount &&
                        steps.includes('joint-account-consent')
                            ? `${partnerName} has finished their application and is waiting for you to join them. `
                            : ''
                    }Our custodian, BNY Mellon Pershing, typically reviews applications within 2-3 business days. We don’t sell your data and we use bank-level encryption to keep your data safe. We also don’t do credit checks, so there is no impact on your credit score.`,
                    consentTitle: `${
                        steps.includes('joint-account-consent') ? '[1/2] ' : ''
                    }Open your Plenty investment account`,
                    consentSubtitle: steps.includes('joint-account-consent')
                        ? 'Each Plenty member gets their own investment account (aka a brokerage account) in addition to any other accounts they’d like to open (like a joint account). This is a one time confirmation to open your account.'
                        : 'This is a one time confirmation to open your individual investment account (aka a personal brokerage account).',
                });
                break;
            case 'joint-account-consent':
                showOpenJointAccountModal({
                    title: `${steps.includes('idv') ? '[2/2] ' : ''}${
                        plentyJointAccountStatus === 'PARTNER_CONSENTED'
                            ? `${partnerName} would like to open a Plenty joint investment account with you`
                            : `Open your Plenty joint investment account with ${partnerName}`
                    }`,
                    showProgressBar: steps.includes('idv'),
                    onComplete: onCompleteStep,
                });
                break;
            case 'complete': {
                const previousStep = getPreviousStep();
                const justCompletedIdv = steps.includes('idv');

                if (previousStep === 'joint-account-consent') {
                    let title: string;
                    let description: string;

                    if (partnerConsentedToJointAccount) {
                        title = 'We’re verifying your identity';
                        description = `We’ll work with BNY Mellon | Pershing to begin opening your ${
                            justCompletedIdv ? 'joint and individual' : 'joint'
                        } investment ${justCompletedIdv ? 'accounts' : 'account'}. You and ${partnerName} will receive an email when it’s ready.`;
                    } else {
                        title = `We’re now waiting for ${partnerName}`;
                        description = `Once ${partnerName} submits their joint account application, we’ll begin opening your joint account with BNY Mellon | Pershing.${
                            justCompletedIdv
                                ? ' We’ll immediately begin opening your individual investment account.'
                                : ''
                        }`;
                    }

                    showSuccessToast({
                        title,
                        description,
                        duration: 10000,
                    });
                } else if (previousStep === 'idv') {
                    showIdvSuccessModal();
                }

                onComplete?.(justCompletedIdv);

                break;
            }
            default: {
                // eslint-disable-next-line @typescript-eslint/no-unused-vars
                const exhaustiveCheck: never = currentStep;
                throw new CustomError('Unknown step', {
                    currentStep,
                });
            }
        }
    };

    return { steps: getSteps(), handleNextStep };
};
